import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Sparkles } from 'lucide-react';
import { isRunning, type Turn } from '../model/conversation';
import { useChatStore } from '../model/chatStore';
import { AnswerBody } from './AnswerBody';
import { LiveDraft } from './LiveDraft';
import { pulseRingSx } from './motion';
import { StepsDisclosure } from './StepsDisclosure';

type Props = { turn: Turn; onAsk?: ((question: string) => void) | undefined; context?: string[] };

export function AssistantMessage({ turn, onAsk, context = [] }: Props) {
  const running = isRunning(turn);

  return (
    <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-start' }}>
      <Box aria-hidden sx={[markSx, running && pulseRingSx]}>
        <Sparkles size={13} strokeWidth={2} />
      </Box>
      <Stack sx={{ gap: 1.5, minWidth: 0, flexGrow: 1, pt: 0.25 }}>
        <StepsDisclosure steps={turn.steps} isRunning={running} startedAt={turn.startedAt} latencyMs={turn.answer?.latencyMs} />
        {turn.answer ? null : <LiveDraft text={turn.draft} isRunning={running} />}
        {turn.answer ? <AnswerBody answer={turn.answer} onAsk={onAsk} question={turn.question} context={context} deskQuestionId={turn.deskQuestionId} onDeskAsked={(id) => { useChatStore.getState().saveDeskQuestion(turn.id, id); }} /> : null}
        {turn.error ? (
          <Alert severity="error" variant="outlined">
            {turn.error}
          </Alert>
        ) : null}
        {turn.stopped ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Stopped before the answer finished.
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

const markSx = {
  width: 24,
  height: 24,
  flexShrink: 0,
  borderRadius: 1,
  display: 'grid',
  placeItems: 'center',
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
} as const;
